/**
 * CRMReports - Analytics dashboard for lead management
 */

import React, { useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import {
  Users,
  UserCheck,
  TrendingUp,
  Clock,
  Calendar,
  Download,
  Filter,
  Loader2,
  ArrowUpRight,
  ArrowDownRight,
} from 'lucide-react';
import {
  useFunnelReport,
  useSourceAnalytics,
  useConversionRates,
  usePerformanceReport,
} from '../../hooks/useCRM';
import { FunnelChart, SourceChart } from '../../components/crm';

// Date range presets
const DATE_RANGES = [
  { value: '30days', label: 'Last 30 Days' },
  { value: '90days', label: 'Last 90 Days' },
  { value: 'thisyear', label: 'This Year' },
  { value: 'custom', label: 'Custom Range' },
];

// Calculate date range based on preset
const getDateRange = (preset) => {
  const today = new Date();
  const endDate = today.toISOString().split('T')[0];
  let startDate;
  
  switch (preset) {
    case '30days':
      startDate = new Date(today.setDate(today.getDate() - 30)).toISOString().split('T')[0];
      break;
    case '90days':
      startDate = new Date(today.setDate(today.getDate() - 90)).toISOString().split('T')[0];
      break;
    case 'thisyear':
      startDate = new Date(today.getFullYear(), 0, 1).toISOString().split('T')[0];
      break;
    default:
      startDate = new Date(today.setDate(today.getDate() - 30)).toISOString().split('T')[0];
  }
  
  return { startDate, endDate };
};

// Stat Card Component
const StatCard = ({ title, value, subtext, icon: Icon, trend, trendValue, color = 'purple' }) => {
  const colorClasses = {
    purple: 'bg-purple-50 text-purple-600 border-purple-100',
    green: 'bg-green-50 text-green-600 border-green-100',
    blue: 'bg-blue-50 text-blue-600 border-blue-100',
    orange: 'bg-orange-50 text-orange-600 border-orange-100',
  };
  
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-gray-500 font-medium">{title}</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{value}</p>
          {subtext && <p className="text-xs text-gray-400 mt-1">{subtext}</p>}
        </div>
        <div className={`p-3 rounded-lg ${colorClasses[color]}`}>
          <Icon size={24} />
        </div>
      </div>
      {trend && (
        <div className="flex items-center mt-4">
          {trend === 'up' ? (
            <ArrowUpRight size={16} className="text-green-500 mr-1" />
          ) : (
            <ArrowDownRight size={16} className="text-red-500 mr-1" />
          )}
          <span className={`text-sm font-medium ${trend === 'up' ? 'text-green-600' : 'text-red-600'}`}>
            {trendValue}
          </span>
          <span className="text-xs text-gray-400 ml-2">vs previous period</span>
        </div>
      )}
    </div>
  );
};

// Staff Performance Table
const StaffPerformanceTable = ({ data, isLoading }) => {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-48">
        <Loader2 className="animate-spin text-purple-600" size={24} />
      </div>
    );
  }
  
  if (!data || data.length === 0) {
    return (
      <div className="text-center py-12 text-gray-500">
        <Users className="mx-auto h-12 w-12 text-gray-300 mb-3" />
        <p>No staff performance data available</p>
      </div>
    );
  }
  
  return (
    <div className="overflow-x-auto -mx-6 px-6">
      <table className="w-full min-w-[600px]">
        <thead>
          <tr className="border-b border-gray-100">
            <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">Staff Member</th>
            <th className="text-center py-3 px-4 text-xs font-medium text-gray-500 uppercase">Leads Assigned</th>
            <th className="text-center py-3 px-4 text-xs font-medium text-gray-500 uppercase">Converted</th>
            <th className="text-center py-3 px-4 text-xs font-medium text-gray-500 uppercase">Conversion Rate</th>
            <th className="text-right py-3 px-4 text-xs font-medium text-gray-500 uppercase">Total Value</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-50">
          {data.map((staff) => (
            <tr key={staff.id} className="hover:bg-gray-50">
              <td className="py-3 px-4">
                <div className="flex items-center">
                  <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center mr-3">
                    <span className="text-sm font-medium text-purple-700">
                      {staff.name?.charAt(0)}
                    </span>
                  </div>
                  <span className="font-medium text-gray-900">{staff.name}</span>
                </div>
              </td>
              <td className="text-center py-3 px-4 text-gray-600">{staff.leadsAssigned}</td>
              <td className="text-center py-3 px-4 text-gray-600">{staff.leadsConverted}</td>
              <td className="text-center py-3 px-4">
                <span
                  className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${
                    staff.conversionRate >= 30
                      ? 'bg-green-100 text-green-700'
                      : staff.conversionRate >= 15
                      ? 'bg-yellow-100 text-yellow-700'
                      : 'bg-gray-100 text-gray-700'
                  }`}
                >
                  {staff.conversionRate?.toFixed(1)}%
                </span>
              </td>
              <td className="text-right py-3 px-4 font-medium text-gray-900">
                ${staff.totalValue?.toLocaleString() || 0}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const CRMReports = () => {
  // eslint-disable-next-line no-unused-vars
  const _tenantId = useParams();
  
  // Date range state
  const [dateRange, setDateRange] = useState('30days');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [showCustomRange, setShowCustomRange] = useState(false);
  
  // Calculate actual dates
  const { startDate, endDate } = useMemo(() => {
    if (dateRange === 'custom' && customStartDate && customEndDate) {
      return { startDate: customStartDate, endDate: customEndDate };
    }
    return getDateRange(dateRange);
  }, [dateRange, customStartDate, customEndDate]);
  
  // Fetch reports data
  const { data: funnelData, isLoading: isLoadingFunnel } = useFunnelReport({
    dateFrom: startDate,
    dateTo: endDate,
  });
  
  const { data: sourceData, isLoading: isLoadingSources } = useSourceAnalytics({
    dateFrom: startDate,
    dateTo: endDate,
  });
  
  const { data: conversionData, isLoading: isLoadingConversion } = useConversionRates({
    dateFrom: startDate,
    dateTo: endDate,
  });
  
  const { data: performanceData, isLoading: isLoadingPerformance } = usePerformanceReport({
    dateFrom: startDate,
    dateTo: endDate,
  });
  
  // Handle date range change
  const handleDateRangeChange = (value) => {
    setDateRange(value);
    setShowCustomRange(value === 'custom');
  };
  
  // Calculate stats
  const stats = useMemo(() => {
    const summary = conversionData?.summary || {};
    const funnel = funnelData?.funnel || [];
    
    const totalLeads = summary.totalLeads || 0;
    // eslint-disable-next-line no-unused-vars
    const _totalConverted = summary.totalConverted || 0;
    const conversionRate = summary.overallConversionRate || 0;
    const avgTimeToConversion = summary.averageTimeToConversion || 0;
    
    // Get enrolled count from funnel
    const enrolledStage = funnel.find((s) => s.stage === 'enrolled');
    const enrolledThisMonth = enrolledStage?.count || 0;
    
    return {
      totalLeads,
      enrolledThisMonth,
      conversionRate: conversionRate.toFixed(1),
      avgTimeToConversion: avgTimeToConversion.toFixed(1),
    };
  }, [conversionData, funnelData]);
  
  // Export report
  const handleExport = () => {
    const reportData = {
      dateRange: { startDate, endDate },
      stats,
      funnel: funnelData?.funnel,
      sources: sourceData?.sources,
      staffPerformance: performanceData?.staff,
    };
    
    const blob = new Blob([JSON.stringify(reportData, null, 2)], {
      type: 'application/json',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `crm-report-${startDate}-to-${endDate}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };
  
  const isLoading = isLoadingFunnel || isLoadingSources || isLoadingConversion || isLoadingPerformance;
  
  return (
    <div className="animate-fade-in space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">CRM Reports</h1>
          <p className="text-gray-500 mt-1">
            Analytics and insights for your lead pipeline
          </p>
        </div>
        
        <div className="flex items-center space-x-3 flex-wrap gap-2">
          {/* Date Range Filter */}
          <div className="flex items-center space-x-2">
            <Filter size={18} className="text-gray-400" />
            <select
              value={dateRange}
              onChange={(e) => handleDateRangeChange(e.target.value)}
              className="form-input py-2"
            >
              {DATE_RANGES.map((range) => (
                <option key={range.value} value={range.value}>
                  {range.label}
                </option>
              ))}
            </select>
          </div>
          
          {/* Export Button */}
          <button
            onClick={handleExport}
            disabled={isLoading}
            className="btn-secondary flex items-center space-x-2"
          >
            <Download size={18} />
            <span>Export</span>
          </button>
        </div>
      </div>
      
      {/* Custom Date Range */}
      {showCustomRange && (
        <div className="bg-white rounded-lg border border-gray-200 p-4 flex flex-wrap items-center gap-4">
          <div>
            <label className="text-xs text-gray-500 font-medium">Start Date</label>
            <input
              type="date"
              value={customStartDate}
              onChange={(e) => setCustomStartDate(e.target.value)}
              className="form-input mt-1"
            />
          </div>
          <div>
            <label className="text-xs text-gray-500 font-medium">End Date</label>
            <input
              type="date"
              value={customEndDate}
              onChange={(e) => setCustomEndDate(e.target.value)}
              className="form-input mt-1"
            />
          </div>
        </div>
      )}
      
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Total Leads"
          value={isLoading ? '-' : stats.totalLeads.toLocaleString()}
          subtext="In selected period"
          icon={Users}
          color="purple"
        />
        <StatCard
          title="Enrolled"
          value={isLoading ? '-' : stats.enrolledThisMonth.toLocaleString()}
          subtext="Successful conversions"
          icon={UserCheck}
          color="green"
        />
        <StatCard
          title="Conversion Rate"
          value={isLoading ? '-' : `${stats.conversionRate}%`}
          subtext="Leads to enrolled"
          icon={TrendingUp}
          color="blue"
        />
        <StatCard
          title="Avg Time to Enroll"
          value={isLoading ? '-' : `${stats.avgTimeToConversion} days`}
          subtext="From lead to enrollment"
          icon={Clock}
          color="orange"
        />
      </div>
      
      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Funnel Chart */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Pipeline Funnel</h2>
              <p className="text-sm text-gray-500">Lead distribution by stage</p>
            </div>
          </div>
          <FunnelChart
            data={funnelData?.funnel}
            isLoading={isLoadingFunnel}
          />
        </div>
        
        {/* Source Chart */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Lead Sources</h2>
              <p className="text-sm text-gray-500">Where your leads come from</p>
            </div>
          </div>
          <SourceChart
            data={sourceData?.sources}
            isLoading={isLoadingSources}
            showConversion
          />
        </div>
      </div>
      
      {/* Staff Performance */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Staff Performance</h2>
            <p className="text-sm text-gray-500">Lead assignment and conversion by staff member</p>
          </div>
        </div>
        <StaffPerformanceTable
          data={performanceData?.staff}
          isLoading={isLoadingPerformance}
        />
      </div>
      
      {/* Conversion Details Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Conversion by Source</h2>
            <p className="text-sm text-gray-500">Which sources convert best</p>
          </div>
        </div>
        
        {isLoadingSources ? (
          <div className="flex items-center justify-center h-48">
            <Loader2 className="animate-spin text-purple-600" size={24} />
          </div>
        ) : sourceData?.sources?.length > 0 ? (
          <div className="overflow-x-auto -mx-6 px-6">
            <table className="w-full min-w-[600px]">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">Source</th>
                  <th className="text-center py-3 px-4 text-xs font-medium text-gray-500 uppercase">Total Leads</th>
                  <th className="text-center py-3 px-4 text-xs font-medium text-gray-500 uppercase">Converted</th>
                  <th className="text-center py-3 px-4 text-xs font-medium text-gray-500 uppercase">Conversion Rate</th>
                  <th className="text-right py-3 px-4 text-xs font-medium text-gray-500 uppercase">Total Value</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {sourceData.sources
                  .sort((a, b) => b.conversionRate - a.conversionRate)
                  .map((source) => (
                    <tr key={source.source} className="hover:bg-gray-50">
                      <td className="py-3 px-4">
                        <span className="font-medium text-gray-900 capitalize">
                          {source.source.replace(/_/g, ' ')}
                        </span>
                      </td>
                      <td className="text-center py-3 px-4 text-gray-600">{source.count}</td>
                      <td className="text-center py-3 px-4 text-gray-600">{source.converted}</td>
                      <td className="text-center py-3 px-4">
                        <div className="flex items-center justify-center">
                          <div className="w-16 bg-gray-100 rounded-full h-2 mr-2">
                            <div
                              className={`h-2 rounded-full ${
                                source.conversionRate >= 30
                                  ? 'bg-green-500'
                                  : source.conversionRate >= 15
                                  ? 'bg-yellow-500'
                                  : 'bg-gray-400'
                              }`}
                              style={{ width: `${Math.min(source.conversionRate, 100)}%` }}
                            />
                          </div>
                          <span className="text-sm font-medium text-gray-700">
                            {source.conversionRate}%
                          </span>
                        </div>
                      </td>
                      <td className="text-right py-3 px-4 font-medium text-gray-900">
                        ${source.totalValue?.toLocaleString() || 0}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-12 text-gray-500">
            <Calendar className="mx-auto h-12 w-12 text-gray-300 mb-3" />
            <p>No source data available for the selected period</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default CRMReports;
